/**
 * What the audio-track screens decide about a track or an upload, apart from any component.
 */

import type { AudioTrackEntry } from '@/api/assets/audioTracks'
import { ApiError } from '@/api/client'

/**
 * Why an upload failed, as the screen words it. The server's message is English and written for a
 * developer, and the transport's names a path; both go to the console, and the row says this.
 */
export type AudioTrackUploadFailure = 'tooLarge' | 'notAudio' | 'refused' | 'network' | 'other'

/** The failure an upload's rejection is, read off its status and code — never off its message. */
export function uploadFailure(error: unknown): AudioTrackUploadFailure {
  if (!(error instanceof ApiError)) {
    // Nothing answered: the transport's `error` event, a dropped connection.
    return 'network'
  }
  if (error.status === 413 || error.code === 'payload_too_large') return 'tooLarge'
  if (error.status === 415 || error.code === 'unsupported_media_type') return 'notAudio'
  if (error.code === 'validation') return 'refused'
  return 'other'
}

/** The first wait before a video's tracks are read again, while one is being prepared. */
export const AUDIO_TRACK_POLL_FIRST_MS = 5_000
/** The longest: a language of the source queued behind other work can be prepared hours later. */
export const AUDIO_TRACK_POLL_MAX_MS = 60_000

/**
 * The tracks being prepared for the player, as one string that changes when one starts or stops
 * being prepared; `''` when none is, which is when nothing is asked again.
 *
 * A track `waiting_for_video` is not among them, and is never polled for: it is encoded, and waits
 * on the video's own encode — which may never come, for a video still playing the previous
 * platform's package. When that encode ends while the page is open, the processing read refreshes
 * every read of the asset, this list included (`useProcessingStep`).
 */
export function preparingTracks(tracks: readonly Pick<AudioTrackEntry, 'uid' | 'playback'>[] | undefined): string {
  return (tracks ?? [])
    .filter((track) => track.playback === 'preparing')
    .map((track) => track.uid)
    .sort()
    .join(',')
}

/**
 * How long to wait before reading the tracks again, `reads` reads after the tracks being prepared
 * last changed: the first wait, doubled after each read that changed nothing, up to the longest.
 */
export function audioTrackPollDelay(reads: number): number {
  return Math.min(AUDIO_TRACK_POLL_FIRST_MS * 2 ** Math.max(0, reads), AUDIO_TRACK_POLL_MAX_MS)
}
